/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/pwa/registerServiceWorker.ts
 *
 * Rôle :
 *   Wrapper autour de `virtual:pwa-register` (vite-plugin-pwa). Demande à
 *   l'utilisateur de mettre à jour quand une nouvelle version est dispo,
 *   plutôt que d'imposer un refresh — cohérent avec les bonnes pratiques
 *   d'une PWA installée à l'écran d'accueil iOS / Android.
 *
 * Réutilisable :
 *   Logique 100 % générique. À copier tel quel dans toute autre PWA Vite.
 *
 * Voir : CLAUDE.md §8.2.3 (stratégies SW), §16.5 (config Workbox).
 *
 * @author Mika
 * @since 2026-05-04
 */

import { registerSW } from 'virtual:pwa-register';
import { afficherBandeauMiseAJour } from './updateBanner';

/**
 * Intervalle de re-vérification d'une nouvelle version.
 *
 * Sans cela, le navigateur ne cherche une mise à jour qu'à la NAVIGATION.
 * Or le back-office est une application d'une seule page : on peut y passer
 * des heures sans jamais changer de document, donc sans jamais découvrir
 * qu'une version plus récente existe. Une heure est un compromis : assez
 * fréquent pour qu'un correctif du matin arrive dans la journée, assez rare
 * pour ne peser sur rien.
 */
const INTERVALLE_VERIF_MS = 60 * 60 * 1000;

/**
 * Enregistre le Service Worker, gère les notifications de mise à jour.
 *
 * @returns Une fonction qui force la mise à jour (à brancher sur un bouton
 *          UI plus tard, dans un écran « Réglages »).
 */
export function registerServiceWorker(): () => void {
  // `registerSW` est injecté par vite-plugin-pwa au build.
  // En dev (`devOptions.enabled: false` cf. vite.config.ts), il est no-op.
  const updateSW = registerSW({
    // 🔴 FIX 2026-09-09 (Mika) — ce point d'entrée utilisait `window.confirm()`.
    // Une boîte modale bloquante, qui interrompt en plein travail : on la
    // ferme d'un réflexe, et l'on reste sur l'ancienne version SANS aucune
    // trace de ce qu'on vient de refuser. Résultat constaté : une recherche
    // livrée le 30/08 et bien EN LIGNE restait invisible dix jours plus tard.
    // Remplacé par un bandeau non bloquant qui PERSISTE tant qu'on n'a pas
    // agi — l'inverse d'un `confirm()`, bloquant et éphémère.
    onNeedRefresh() {
      afficherBandeauMiseAJour(() => {
        void updateSW(true);
      });
    },
    onOfflineReady() {
      // L'app est désormais utilisable hors-ligne. Pas d'alerte intrusive.
      console.info('[TNE 2026] Application disponible hors ligne.');
    },
    onRegisterError(error) {
      console.error('[TNE 2026] Erreur enregistrement Service Worker :', error);
    },
    onRegisteredSW(_url, registration) {
      // Re-vérification périodique : voir INTERVALLE_VERIF_MS. Le back-office
      // étant une application d'une seule page, sans cela un administrateur
      // resté connecté ne découvrirait JAMAIS une nouvelle version.
      if (registration === undefined) {
        return;
      }
      window.setInterval(() => {
        // `update()` peut échouer (hors ligne, serveur momentanément absent) :
        // c'est sans gravité, on retentera au prochain tour. On ne laisse pas
        // pour autant la promesse se rejeter dans le vide.
        void registration.update().catch(() => undefined);
      }, INTERVALLE_VERIF_MS);
    },
  });

  return () => void updateSW(true);
}
